import { useEffect, useMemo, useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { api, directoryApi, type DirectoryAgent } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { t } from '../../lib/i18n';
import type { Inbox } from '../../lib/types';
import { InboxPicker } from './InboxPicker';
import { sx } from './styles';

type Strategy = 'round-robin' | 'least-loaded' | 'pinned';

interface AssignmentConfig {
  strategy: Strategy;
  pinnedAgentUid?: string;
  agentSkills?: Record<string, string[]>; // agentUid -> tags
}

interface InboxWithAssignment extends Inbox {
  assignment?: Partial<AssignmentConfig>;
}

const DEFAULT: AssignmentConfig = {
  strategy: 'round-robin',
  pinnedAgentUid: undefined,
  agentSkills: {},
};

export function AssignmentPanel({ canEdit }: { canEdit: boolean }) {
  const { items: inboxes, loading } = useResource<InboxWithAssignment>('inboxes');
  const { claims } = useAuth();
  const tenantCode = claims?.tenantCode;
  const [inboxId, setInboxId] = useState<string | null>(null);
  const [draft, setDraft] = useState<AssignmentConfig>(DEFAULT);
  // The inbox's agent pool — the set of agents auto-assignment chooses among.
  // Editable here (an agent can be removed from the rotation).
  const [agents, setAgents] = useState<string[]>([]);
  const [dir, setDir] = useState<DirectoryAgent[]>([]);
  const [saving, setSaving] = useState(false);

  const selected = useMemo(() => inboxes.find((i) => i.id === inboxId) || null, [inboxes, inboxId]);

  useEffect(() => {
    if (!inboxId && inboxes.length > 0) setInboxId(inboxes[0].id);
  }, [inboxes, inboxId]);

  // Resolve agent UIDs → display names.
  useEffect(() => {
    if (!tenantCode) return;
    directoryApi.listAgentsDirectory(tenantCode).then((r) => setDir(r.items));
  }, [tenantCode]);

  useEffect(() => {
    if (!selected) return;
    const a = selected.assignment;
    setDraft({
      strategy: (a?.strategy as Strategy) || DEFAULT.strategy,
      pinnedAgentUid: a?.pinnedAgentUid,
      agentSkills: (a?.agentSkills as Record<string, string[]>) || {},
    });
    setAgents(selected.agentUids ?? []);
  }, [selected]);

  const nameFor = (uid: string) => dir.find((d) => d.uid === uid)?.displayName || uid.slice(0, 12);

  async function save() {
    if (!inboxId) return;
    setSaving(true);
    try {
      // Persist both the strategy/skills config AND the (possibly trimmed) agent pool.
      await api.patch('inboxes', inboxId, { assignment: draft, agentUids: agents });
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  function setSkills(agentUid: string, tags: string[]) {
    setDraft((d) => ({
      ...d,
      agentSkills: { ...(d.agentSkills ?? {}), [agentUid]: tags },
    }));
  }

  function removeAgent(uid: string) {
    setAgents((a) => a.filter((u) => u !== uid));
    setDraft((d) => {
      const skills = { ...(d.agentSkills ?? {}) };
      delete skills[uid];
      return {
        ...d,
        agentSkills: skills,
        pinnedAgentUid: d.pinnedAgentUid === uid ? undefined : d.pinnedAgentUid,
      };
    });
  }

  const strategyHint =
    draft.strategy === 'round-robin'
      ? 'Round robin hands each new conversation to the next agent in the list below, cycling through them in turn (online agents are preferred). Remove an agent below to take them out of the rotation.'
      : draft.strategy === 'least-loaded'
        ? 'Least loaded sends each new conversation to whichever agent below currently has the fewest open conversations.'
        : 'Pinned routes every new conversation to one specific agent.';

  return (
    <section>
      <h2 style={{ margin: 0, fontSize: 18, marginBottom: 12 }}>{t('settings.assignment.title')}</h2>
      {loading && <div>{t('common.loading')}</div>}
      <InboxPicker inboxes={inboxes} value={inboxId} onChange={setInboxId} />

      {selected && (
        <>
          <div style={sx.card}>
            <label style={sx.label}>{t('settings.assignment.strategy')}</label>
            {(['round-robin', 'least-loaded', 'pinned'] as Strategy[]).map((s) => (
              <label key={s} style={{ display: 'block', fontSize: 13, padding: '4px 0' }}>
                <input
                  type="radio"
                  checked={draft.strategy === s}
                  onChange={() => setDraft((d) => ({ ...d, strategy: s }))}
                  disabled={!canEdit}
                  style={{ marginRight: 6 }}
                />
                {t(
                  s === 'round-robin'
                    ? 'settings.assignment.strategy.roundRobin'
                    : s === 'least-loaded'
                      ? 'settings.assignment.strategy.leastLoaded'
                      : 'settings.assignment.strategy.pinned'
                )}
              </label>
            ))}
            <p style={{ ...sx.hint, marginTop: 8 }}>{strategyHint}</p>
          </div>

          {draft.strategy === 'pinned' && (
            <div style={sx.card}>
              <label style={sx.label}>{t('settings.assignment.pinnedAgent')}</label>
              <select
                value={draft.pinnedAgentUid ?? ''}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, pinnedAgentUid: e.target.value || undefined }))
                }
                disabled={!canEdit}
                style={{ ...sx.input, maxWidth: 320 }}
              >
                <option value="">—</option>
                {agents.map((uid) => (
                  <option key={uid} value={uid}>
                    {nameFor(uid)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={sx.card}>
            <label style={sx.label}>
              {t('settings.assignment.skills')} — agents in the rotation
            </label>
            <p style={sx.hint}>{t('settings.assignment.skills.placeholder')}</p>
            <table style={sx.table}>
              <thead>
                <tr>
                  <th style={sx.th}>{t('reports.agents.agent')}</th>
                  <th style={sx.th}>{t('settings.assignment.skills')}</th>
                  {canEdit && <th style={{ ...sx.th, width: 70 }}></th>}
                </tr>
              </thead>
              <tbody>
                {agents.length === 0 && (
                  <tr>
                    <td colSpan={canEdit ? 3 : 2} style={{ ...sx.td, color: '#9ca3af' }}>
                      {t('common.noData')}
                    </td>
                  </tr>
                )}
                {agents.map((uid) => {
                  const tags = draft.agentSkills?.[uid] ?? [];
                  return (
                    <tr key={uid}>
                      <td style={sx.td} title={uid}>
                        {nameFor(uid)}
                      </td>
                      <td style={sx.td}>
                        <input
                          type="text"
                          value={tags.join(', ')}
                          onChange={(e) =>
                            setSkills(
                              uid,
                              e.target.value
                                .split(',')
                                .map((s) => s.trim())
                                .filter(Boolean)
                            )
                          }
                          disabled={!canEdit}
                          style={sx.input}
                        />
                      </td>
                      {canEdit && (
                        <td style={sx.td}>
                          <button
                            onClick={() => removeAgent(uid)}
                            style={{
                              background: 'transparent',
                              border: '1px solid #fca5a5',
                              color: '#dc2626',
                              borderRadius: 4,
                              fontSize: 11,
                              padding: '2px 8px',
                              cursor: 'pointer',
                            }}
                            title="Remove this agent from the inbox / auto-assignment rotation"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {canEdit && (
              <p style={{ ...sx.hint, marginTop: 6 }}>
                Removing an agent takes them out of this inbox's auto-assignment rotation (click Save
                to apply). Add agents back from the Inboxes settings.
              </p>
            )}
          </div>

          {canEdit && (
            <button onClick={save} disabled={saving} style={sx.btnPrimary}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
          )}
        </>
      )}
    </section>
  );
}
